import { createContext, useContext, useMemo } from 'react'
import {
  resolveBacktestRunLimits,
  type BacktestRunLimits,
  type PlanTier,
  type Feature,
} from './plan-constraint-utils'
import type { Usage, Entitlements } from '@/types/schemas/api'

// ─── Context ───

/**
 * Resolved plan constraints for the signed-in user.
 *
 * Every numeric limit here is already RESOLVED: the server payload when it has
 * arrived, the DEFAULT_* table for the derived tier until then (see
 * `plan-constraint-utils.ts`). Unlimited arrives from the server as `null` and is
 * resolved to `Infinity`, so `count < limit` comparisons need no special case.
 * Read a limit from these fields, never from a per-tier table in a component.
 */
export interface PlanConstraintContextValue {
  /** Check if a feature is available on the current plan. */
  hasAccess: (feature: Feature) => boolean
  /** Current plan tier. */
  planTier: PlanTier
  /** Current plan name (null if no subscription). */
  planName: string | null
  /** Max custom layouts allowed on current plan. `Infinity` when unlimited. */
  maxCustomLayouts: number
  /** Max rows a scanner query returns. */
  scannerRowLimit: number
  /** Fastest scanner refresh cadence, in ms. */
  scannerPollIntervalMs: number
  /** Scanner panels allowed in one layout. Infinity when the plan is unlimited. */
  scannerPanelsPerLayoutMax: number
  /** Max user-defined expressions per namespace. */
  maxExpressions: number
  /** Server-side usage tracking (null if not loaded yet). */
  usage: Usage | null
  /**
   * The raw server payload, or null before it lands / in mock auth. Read this
   * only for a limit the resolved fields above do not cover yet (export and
   * backtest caps, history window).
   */
  entitlements: Entitlements | null
}

export const PlanConstraintContext = createContext<PlanConstraintContextValue | null>(null)

export function usePlanConstraint(): PlanConstraintContextValue {
  const ctx = useContext(PlanConstraintContext)
  if (!ctx) {
    throw new Error('usePlanConstraint must be used within PlanConstraintProvider')
  }
  return ctx
}

// ─── Convenience Hooks ───

export function usePlanGate() {
  const { hasAccess, planTier, maxCustomLayouts } = usePlanConstraint()
  return {
    planTier,
    canCreateCustomLayout: hasAccess('history-custom-layouts'),
    maxCustomLayouts,
    hasAccess,
  }
}

/** Max rows a scanner query may return on the current plan. */
export function useScannerRowLimit(): number {
  return usePlanConstraint().scannerRowLimit
}

/** Fastest scanner refresh cadence (ms) the current plan allows. */
export function useScannerPollIntervalMs(): number {
  return usePlanConstraint().scannerPollIntervalMs
}

/** Scanner panels allowed in one layout. `Infinity` on the unlimited tiers. */
export function useScannerPanelsPerLayoutMax(): number {
  return usePlanConstraint().scannerPanelsPerLayoutMax
}

/** Max user-defined expressions per namespace on the current plan. */
export function useMaxExpressions(): number {
  return usePlanConstraint().maxExpressions
}

/** A batch backtest's ticker-day and lookback caps on the current plan. `Infinity` is none. */
export function useBacktestRunLimits(): BacktestRunLimits {
  const { entitlements, planTier } = usePlanConstraint()
  return useMemo(() => resolveBacktestRunLimits(entitlements, planTier), [entitlements, planTier])
}
